import { Duration } from "luxon";
import { useContext, useEffect, useRef } from "react";

import { DataContext } from "@/models/DataProvider";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";

/**
 * How often availability is re-polled: location-only changes raise no AppSync signal, so this timer carries them.
 *
 * One minute is safe against the incremental sync (`syncWorkspaceMembershipAvailabilities`). Each poll asks for the
 * rows updated since the previous poll's start, less the cursor's skew buffer, so consecutive polls overlap rather
 * than leave a gap whatever the interval, and the overlap re-upserts rows in place. A poll carries only the rows that
 * moved since the last one, well inside a single page walk's limit. A tick that lands while a walk is still paging, a
 * long daily full sweep included, waits for it rather than starting a second walk beside it.
 */
const AVAILABILITY_REFRESH = Duration.fromObject({ minutes: 1 });

export default function UseWorkspaceMembershipAbilities() {
  const { fetchWorkspaceMembershipAvailabilities } = useContext(DataContext);
  const { currentWorkspaceId } = useContext(WorkspaceContext);
  const workspaceIdRef = useRef(currentWorkspaceId);
  workspaceIdRef.current = currentWorkspaceId;
  const fetchRef = useRef(fetchWorkspaceMembershipAvailabilities);
  fetchRef.current = fetchWorkspaceMembershipAvailabilities;

  useEffect(() => {
    if (!currentWorkspaceId) return;

    const interval = setInterval(() => {
      fetchRef.current?.(workspaceIdRef.current)?.catch(() => {});
    }, AVAILABILITY_REFRESH.toMillis());

    return () => clearInterval(interval);
  }, [currentWorkspaceId]);
}
